import { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, CreditCard, Lock, Shield } from 'lucide-react';
import { useBodyScrollLock } from '../hooks/useBodyScrollLock';
import { api } from '../services/api';
import { normalizeDisplayCurrency } from '../constants/currencies';

interface PaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => Promise<void>;
  bookingId: string;
  paymentLink?: string | null;
  amount: number;
  currency: string;
  courseName?: string;
}

const PaymentModal = ({ isOpen, onClose, onConfirm, bookingId, paymentLink, amount, currency, courseName }: PaymentModalProps) => {
  const [error, setError] = useState<string | null>(null);
  const [isPayPalReady, setIsPayPalReady] = useState(false);
  const paypalContainerRef = useRef<HTMLDivElement>(null);
  const paypalRenderedRef = useRef(false);
  const internalOrderIdRef = useRef<string | null>(null);

  useBodyScrollLock(isOpen);

  const formatCurrency = (value: number, curr: string) => {
    const code = normalizeDisplayCurrency(curr);
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: code,
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(value);
  };

  const loadPayPalScript = (clientId: string) => {
    if ((window as any).paypal) {
      return Promise.resolve();
    }

    return new Promise<void>((resolve, reject) => {
      const existingScript = document.querySelector<HTMLScriptElement>(
        'script[src^="https://www.paypal.com/sdk/js"]'
      );
      if (existingScript) {
        existingScript.addEventListener('load', () => resolve());
        existingScript.addEventListener('error', () => reject(new Error('Failed to load PayPal SDK')));
        return;
      }

      const script = document.createElement('script');
      script.src = `https://www.paypal.com/sdk/js?client-id=${clientId}`;
      script.async = true;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error('Failed to load PayPal SDK'));
      document.body.appendChild(script);
    });
  };

  useEffect(() => {
    if (!isOpen) {
      setIsPayPalReady(false);
      paypalRenderedRef.current = false;
      internalOrderIdRef.current = null;
      if (paypalContainerRef.current) {
        paypalContainerRef.current.innerHTML = '';
      }
      return;
    }

    if (!paymentLink) {
      return;
    }

    const clientId = import.meta.env.VITE_PAYPAL_CLIENT_ID as string | undefined;
    if (!clientId) {
      setError('PayPal client ID is not configured.');
      return;
    }

    loadPayPalScript(clientId)
      .then(() => setIsPayPalReady(true))
      .catch((err) => setError(err.message || 'Failed to load PayPal.'));
  }, [isOpen, paymentLink]);

  useEffect(() => {
    if (!isPayPalReady || !paymentLink || !paypalContainerRef.current || paypalRenderedRef.current) {
      return;
    }

    const paypal = (window as any).paypal;
    if (!paypal?.Buttons) {
      setError('PayPal SDK is not available.');
      return;
    }

    paypal
      .Buttons({
        createOrder: async () => {
          const response = await api.post('/v1/payment/paypal/orders', {
            bookingId,
            amount,
            currency_code: currency || 'USD',
          });

          if (!response.data?.success) {
            throw new Error(response.data?.message || 'Failed to create PayPal order.');
          }

          const { orderId, paypalOrderId } = response.data.data;
          internalOrderIdRef.current = orderId;
          return paypalOrderId;
        },
        onApprove: async () => {
          const internalOrderId = internalOrderIdRef.current;
          if (!internalOrderId) {
            throw new Error('Payment order not initialized.');
          }

          try {
            const captureResponse = await api.post(
              `/v1/payment/paypal/orders/${internalOrderId}/capture`
            );
            if (!captureResponse.data?.success) {
              throw new Error(captureResponse.data?.message || 'Payment capture failed.');
            }

            await onConfirm();
          } finally {
            // Always close the modal to return user to the calling page
            onClose();
          }
        },
        onError: (err: any) => {
          setError(err?.message || 'Payment failed. Please try again.');
        },
      })
      .render(paypalContainerRef.current);

    paypalRenderedRef.current = true;
  }, [isPayPalReady, paymentLink, bookingId, amount, currency, onConfirm, onClose]);

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            width: '100vw',
            height: '100vh',
            zIndex: 20000,
            touchAction: 'none',
            overflow: 'hidden',
            overscrollBehavior: 'none',
          }}
          onTouchStart={(e) => {
            if (e.target === e.currentTarget) {
              e.preventDefault();
              e.stopPropagation();
            }
          }}
          onTouchMove={(e) => {
            if (e.target === e.currentTarget) {
              e.preventDefault();
              e.stopPropagation();
            }
          }}
          onClick={onClose}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-hidden"
            style={{
              touchAction: 'pan-y',
              overscrollBehavior: 'contain',
              WebkitOverflowScrolling: 'touch',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-primary to-primary-dark p-6 text-white">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                    <CreditCard className="h-6 w-6" />
                  </div>
                  <div>
                    <h2 className="text-5 font-semibold">Secure Payment</h2>
                    <p className="text-2 text-white/80">Complete your booking</p>
                  </div>
                </div>
                <button
                  onClick={onClose}
                  className="p-2 hover:bg-white/20 rounded-full transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-6 space-y-6" style={{ maxHeight: 'calc(90vh - 200px)', overflowY: 'auto' }}>
              {/* Booking Info */}
              {courseName && (
                <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                  <p className="text-2 text-gray-600 mb-1">Course</p>
                  <p className="font-semibold text-gray-900">{courseName}</p>
                </div>
              )}

              {/* Amount Display */}
              <div className="bg-gradient-to-br from-primary/10 to-primary/5 rounded-xl p-6 border-2 border-primary/20">
                <div className="text-center">
                  <p className="text-2 text-gray-600 mb-2">Total Amount</p>
                  <p className="text-5 font-semibold text-primary mb-1">
                    {formatCurrency(amount, currency)}
                  </p>
                  <p className="text-1 text-gray-500">Initial booking amount</p>
                </div>
              </div>

              {/* PayPal Payment */}
              <div className="space-y-4">
                {paymentLink ? (
                  <div className="border border-gray-200 rounded-xl p-4 bg-white">
                    <div className="flex items-center gap-2 mb-3">
                      <Lock className="h-5 w-5 text-gray-500" />
                      <p className="text-2 font-semibold text-gray-700">Pay securely with PayPal</p>
                    </div>
                    <div ref={paypalContainerRef} />
                  </div>
                ) : (
                  <div className="border border-amber-200 rounded-xl p-4 bg-amber-50 text-2 text-amber-800">
                    Payment link is not available yet. Please wait for the school to approve your booking.
                  </div>
                )}

                <div className="flex items-center justify-center gap-4 text-1 text-gray-600">
                  <div className="flex items-center gap-1">
                    <Shield className="h-4 w-4 text-green-600" />
                    <span>SSL Secured</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Lock className="h-4 w-4 text-green-600" />
                    <span>256-bit Encryption</span>
                  </div>
                </div>
              </div>

              {/* Error Message */}
              {error && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-center gap-3"
                >
                  <X className="h-5 w-5 text-red-600 flex-shrink-0" />
                  <p className="text-2 text-red-800">{error}</p>
                </motion.div>
              )}
            </div>

            {/* Footer Actions */}
            <div className="border-t border-gray-200 p-6 bg-gray-50">
              <div className="flex gap-3">
                <button
                  onClick={onClose}
                  className="flex-1 px-6 py-3 bg-white border-2 border-gray-300 text-gray-700 rounded-xl font-semibold hover:bg-gray-50 transition-all"
                >
                  Cancel
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default PaymentModal;

